/**
 * PartTypes.jsx — "which of these is a printed guide?"
 *
 * The engine cannot answer this and does not try. Nothing in the team's files
 * identifies a fabricated part: the names carry no descriptor (`Page 17.OBJ`),
 * and the paint cannot separate them either — `Page 12.OBJ` (a splint) and
 * `Page 3 - Native skull.OBJ` are BOTH 100% #FFFFFF. A filename rule was tried
 * and fired on 0 of 30 meshes. So the operator declares, exactly as `picks`
 * resolves two meshes claiming one slot.
 *
 * Two levels, because the team's exports come both ways:
 *   · the whole FILE is one part            — `Page 17.OBJ`
 *   · one file holds a part AND its base    — `Page 20 - Genioplasty…guide.OBJ`
 *     is the guide, the trial-fitment mandible and the screw inserts, separated
 *     by POLYGROUP. Declaring that whole file a guide turns the anatomy inside
 *     it white, which is precisely the defect this control exists to prevent.
 *
 * Polygroups are read on demand, not up front: the corpus skull is 125 MB and
 * scanning every file on load would stall the page for meshes nobody is going
 * to touch.
 */
import { useState } from 'react'

import { PART_PRESETS } from '../../lib/caseReport/palette.js'
import { ROLE_LABELS } from '../../lib/caseReport/classify.js'
import { readPolygroups } from '../../lib/caseReport/objGroups.js'

const sel = 'border border-gray-300 rounded px-1.5 py-0.5 text-xs bg-white'

export default function PartTypes({ library, parts, setParts }) {
  const [groups, setGroups] = useState({})     // meshId → string[] | 'loading' | 'none'
  const [open, setOpen] = useState({})

  if (!library.length) return null

  // A declaration is either a preset id (whole mesh) or a map of group → id.
  const wholeId = (m) => (typeof parts[m.id] === 'string' ? parts[m.id] : '')
  const groupId = (m, g) => (typeof parts[m.id] === 'object' ? parts[m.id]?.[g] ?? '' : '')

  const setWhole = (m, id) => setParts((p) => {
    const next = { ...p }
    if (id) next[m.id] = id
    else delete next[m.id]                     // back to auto — never an empty record
    return next
  })

  const setGroup = (m, g, id) => setParts((p) => {
    // A whole-file preset already in force is the operator's statement about
    // every group in the file. Starting the map from {} threw it away and let
    // the untouched groups fall back to the role default, with the now-disabled
    // whole-file select hiding what had been lost. Seed the map from it instead.
    const prior = p[m.id]
    const cur = typeof prior === 'object'
      ? { ...prior }
      : (typeof prior === 'string' && prior
        ? Object.fromEntries((groups[m.id] ?? []).map((n) => [n, prior]))
        : {})
    if (id) cur[g] = id
    else delete cur[g]
    const next = { ...p }
    if (Object.keys(cur).length) next[m.id] = cur
    else delete next[m.id]
    return next
  })

  async function toggle(m) {
    const isOpen = !open[m.id]
    setOpen((o) => ({ ...o, [m.id]: isOpen }))
    if (!isOpen || groups[m.id]) return
    if (!m.handle) { setGroups((g) => ({ ...g, [m.id]: 'none' })); return }
    setGroups((g) => ({ ...g, [m.id]: 'loading' }))
    try {
      const names = await readPolygroups(m.handle)
      setGroups((g) => ({ ...g, [m.id]: names.length > 1 ? names : 'none' }))
    } catch {
      setGroups((g) => ({ ...g, [m.id]: 'none' }))
    }
  }

  const declared = Object.keys(parts).length

  return (
    <div className="rounded-xl border border-gray-200 p-3">
      <span className="text-sm font-semibold text-gray-700 block">
        Part types{declared ? ` — ${declared} declared` : ''}
      </span>
      <p className="text-[11px] text-gray-500 mb-2">
        Guides and splints print in white resin. Nothing in the files says which mesh is
        which, so the report leaves them as detected until you say otherwise.
      </p>

      <div className="space-y-1 max-h-72 overflow-y-auto">
        {library.map((m) => {
          const g = groups[m.id]
          const isMap = typeof parts[m.id] === 'object'
          return (
            <div key={m.id} className="text-xs border-b border-gray-100 last:border-0 pb-1">
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => toggle(m)}
                  className="w-4 shrink-0 text-gray-400 hover:text-gray-700"
                  title="Show the polygroups inside this file"
                  data-testid={`parttype-expand-${m.id}`}>
                  {open[m.id] ? '▾' : '▸'}
                </button>
                <span className="flex-1 truncate text-gray-600" title={m.name}>{m.name}</span>
                <span className="w-24 shrink-0 text-gray-400 truncate" title="what the classifier detected">
                  {ROLE_LABELS[m.role] ?? m.role}
                </span>
                <select className={sel} value={wholeId(m)} disabled={isMap}
                  title={isMap ? 'Set per polygroup below' : 'Applies to the whole file'}
                  data-testid={`parttype-${m.id}`}
                  onChange={(e) => setWhole(m, e.target.value)}>
                  {PART_PRESETS.map((p) => (
                    <option key={p.id || 'auto'} value={p.id}>{p.label}</option>
                  ))}
                </select>
              </div>

              {open[m.id] && (
                <div className="pl-6 pt-1 space-y-0.5">
                  {g === 'loading' && <span className="text-gray-400">reading polygroups…</span>}
                  {g === 'none' && (
                    <span className="text-gray-400">
                      one part — use the dropdown above
                    </span>
                  )}
                  {Array.isArray(g) && g.map((name) => (
                    <div key={name} className="flex items-center gap-2">
                      <span className="flex-1 truncate text-gray-500">{name}</span>
                      <select className={sel} value={groupId(m, name)}
                        data-testid={`parttype-${m.id}-${name}`}
                        onChange={(e) => setGroup(m, name, e.target.value)}>
                        {PART_PRESETS.map((p) => (
                          <option key={p.id || 'auto'} value={p.id}>{p.label}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
